// eslint-disable-next-line no-restricted-imports
import { type ComponentProps, type ComponentType, lazy, type ReactNode } from "react";

import { BTSuspense } from "commonComponents/btWrappers/BTSuspense/BTSuspense";

const RETRY_DELAY_MS = 1500;

/**
 * Named `ChunkLoadError` so `ErrorBoundary` shows its "Fail to load page" alert with a Reload
 * button instead of the generic error text.
 */
class LazyComponentLoadError extends Error {
    constructor(message: string) {
        super(message);
        this.name = "ChunkLoadError";
    }
}

const hasDefaultComponent = (loaded: { default?: unknown } | null | undefined): boolean =>
    loaded?.default !== undefined && loaded.default !== null;

/**
 * This helper will accept the component to load and return a component wrapped in a BTSuspense component.
 * A failed import, or one that resolves without a default component, is retried once. If the retry
 * fails too, the error is thrown at render so the nearest error boundary handles it.
 * @param factory The import path for the component you want to load
 * @param options.fallback Shown while the chunk loads, in place of BTSuspense's full-viewport spinner
 * @returns the component, wrapped in a BTSuspense component
 * @example `const Bill = lazyLoadComponent(() => import("entity/bill/Bill/Bill"));`
 */
export function lazyLoadComponent<T extends ComponentType<any>>(
    factory: () => Promise<{ default: T }>,
    options: { fallback?: ReactNode } = {},
) {
    const loadComponent = () =>
        new Promise<{ default: T }>((resolve, reject) => {
            const attempt = (isRetry: boolean) => {
                const fail = (error: unknown) => {
                    if (isRetry) {
                        reject(error instanceof Error ? error : new Error(String(error)));
                        return;
                    }
                    // retry after some time
                    void setTimeout(() => attempt(true), RETRY_DELAY_MS);
                };

                factory().then((loaded) => {
                    if (hasDefaultComponent(loaded)) {
                        resolve(loaded);
                        return;
                    }
                    // Without this check React throws "Element type is invalid" with no hint of
                    // which import it was. The factory source holds the chunk name in a production build.
                    fail(
                        new LazyComponentLoadError(
                            `Lazy import resolved without a default component: ${factory.toString().slice(0, 300)}`,
                        ),
                    );
                }, fail);
            };
            attempt(false);
        });
    const LazyComponent = lazy(loadComponent);
    return (props: ComponentProps<T>) => (
        <BTSuspense fallback={options.fallback}>
            <LazyComponent {...props} />
        </BTSuspense>
    );
}
